import React, { useEffect, useState } from "react";
import { Plan } from "models/Plan";
import { Modals } from "constants/modals";
import { useAppContext } from "hooks/app";
import useBillingContext from "../../pages/UserDashboard/Billing/hooks/useBillingContext";
import { useSubscription, useUser } from "hooks/user";
import { useGetManualInvoices } from "hooks/billing";
import { InvoiceStatus } from "enum/invoiceStatus";
import { PlanType } from "enum/planTypes";
import { usePlans } from "hooks/plan";
import "./style.scss";
import { useNavigate, useSearchParams } from "react-router-dom";
import { trackLifetimeCheckoutClick } from "utils/tracking";
import useLifetimePromo from "hooks/useLifetimePromo";
import useCountdownTimer from "hooks/useCountdownTimer";
import { useCaptureAbandonedCheckout } from "hooks/abandonedCheckout";
import { useVerifyCoupon } from "hooks/coupon";
import QuotaList from "components/QuotaList/QuotaList";

interface ILifetimePlan {
  page: string
}
const LifetimePlanCard: React.FC<ILifetimePlan> = ({ page }) => {
  const { plans, isLoading } = usePlans();
  const [lifetimePlan, setLifetimePlan] = useState<Plan | null>(null);
  const { openModal } = useAppContext();
  const { setActivePlan, selectedPlan } = useBillingContext();
  const { subscription } = useSubscription();
  const { user } = useUser();
  const { manualInvoices, isLoading: loadingManualInvoices } = useGetManualInvoices();
  const [basicPlanIsInReview, setBasicPlanIsInReview] = useState(false);
  const navigate = useNavigate();
  const { discountPct, slotsLeft, discountedPrice } = useLifetimePromo();
  const { hours, minutes, seconds } = useCountdownTimer();
  const { capture } = useCaptureAbandonedCheckout();
  const { couponVerify } = useVerifyCoupon();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (plans?.length) {
      const foundPlan = plans.find((plan) => plan.interval === "lifetime");
      if (foundPlan) setLifetimePlan(foundPlan);
    }
  }, [plans]);

  useEffect(() => {
    if (!loadingManualInvoices && manualInvoices?.length) {
      setBasicPlanIsInReview(
        manualInvoices[0]?.status === InvoiceStatus.IN_REVIEW &&
          manualInvoices[0]?.planType === PlanType.LIFETIME
      );
    }
  }, [manualInvoices, loadingManualInvoices]);

  // Auto-apply coupon code from URL param (e.g. from recovery email CTA)
  useEffect(() => {
    const couponFromUrl = searchParams.get("coupon");
    if (couponFromUrl && lifetimePlan?.id) {
      couponVerify(couponFromUrl, lifetimePlan.id);
    }
  }, [searchParams, lifetimePlan?.id]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] text-gray-500">
        Loading...
      </div>
    );
  }
  if (!lifetimePlan) return null;

  const choosePlan = () => {
    trackLifetimeCheckoutClick(lifetimePlan?.name ?? 'Lifetime Plan', Number(lifetimePlan?.price ?? 0));

    if (!user?.id) {
      // Non-logged-in: capture lead via modal, stop here
      openModal?.(Modals.LEAD_CAPTURE, { planType: "4", planName: lifetimePlan?.name ?? "Lifetime Plan" });
      return;
    }

    // Logged-in: silently capture abandonment, then proceed to checkout
    capture({ email: user.email, source: page, plan: lifetimePlan?.name ?? undefined });

    if (!subscription || page === "cta")
      navigate("/user/billing");
    setActivePlan?.(0);
    selectedPlan?.(
      lifetimePlan.name,
      lifetimePlan.description,
      lifetimePlan.price,
      lifetimePlan.interval,
      lifetimePlan.interval_count,
      lifetimePlan.id
    );
    openModal?.(Modals.PAYMENT_METHOD);
  };
  const isLifetimeActive = (subscription?.planType === PlanType.LIFETIME && subscription.status) ? true : false;
  const buttonDisabled =
    (isLifetimeActive) || basicPlanIsInReview;

  return (
    <div className={`lifetime-card ${isLifetimeActive ? "active" : ""}`}>
      {/* Left Section */}
      <div className="lifetime-left">
        <div className="offer-tag">{discountPct > 0 ? `${discountPct}% Off — Limited Time` : 'Limited Time Offer'}</div>
        {isLifetimeActive && <div className="active-tag">Plan Active</div>}
        <h2>Lifetime Plan</h2>
        <p className="punchline">
          One-time payment, lifetime access <br /> No monthly fees – Ever!
        </p>
        {discountPct > 0 ? (
          <>
            <p className="original-price">Rs. {Number(lifetimePlan.price).toLocaleString()}</p>
            <p className="price">Rs. {discountedPrice(Number(lifetimePlan.price)).toLocaleString()}</p>
            <div className="discount-timer">
              <span className="discount-badge">{discountPct}% OFF</span>
              <span className="timer-inline">{hours}:{minutes}:{seconds} left</span>
            </div>
          </>
        ) : (
          <>
            {lifetimePlan.originalPrice && Number(lifetimePlan.originalPrice) > Number(lifetimePlan.price) && (
              <p className="original-price">Rs. {Number(lifetimePlan.originalPrice).toLocaleString()}</p>
            )}
            <p className="price">Rs. {Number(lifetimePlan.price).toLocaleString()}</p>
          </>
        )}
        {slotsLeft !== null && (
          <p className="availability">Only {slotsLeft} slots left</p>
        )}

        <button
          disabled={buttonDisabled}
          onClick={choosePlan}
          className={`btn-primary ${
            buttonDisabled
              ? "bg-white text-blue-700 border-blue-700 cursor-not-allowed"
              : ""
          }`}
        >
          Get Lifetime Access
        </button>
      </div>

      {/* Right Section */}
      <div className="lifetime-right">
        <h3>What’s Included</h3>
        <div className="features-list">
          <div className="quota-list-wrap">
            <QuotaList plan={lifetimePlan} variant="card" title="Monthly Quotas" valueSuffix="/mo" />
          </div>

          <h4>Unlimited Tools</h4>
          <ul>
            <li>Unlimited Product Discovery</li>
            <li>Unlimited Store Discovery</li>
            <li>Unlimited Daraz Profit Calculator</li>
            <li>Unlimited Product Rank Finder</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default LifetimePlanCard;
